import React from "react";
import { motion } from "framer-motion";
import { Check, Trash2, Plus, Minus } from "lucide-react";
import FreshnessBadge from "./FreshnessBadge";

const stripClass = {
  red: "bg-fresh-red",
  yellow: "bg-fresh-yellow",
  green: "bg-fresh-green",
  expired: "bg-zinc-400",
};

const qtyTextClass = {
  red: "text-fresh-red-text",
  yellow: "text-fresh-yellow-text",
  green: "text-fresh-green-text",
  expired: "text-zinc-700",
};

const qtyBgClass = {
  red: "bg-fresh-red-soft",
  yellow: "bg-fresh-yellow-soft",
  green: "bg-fresh-green-soft",
  expired: "bg-zinc-100",
};

export function PantryRow({ item, category, onOpen, onConsume, onDelete, onDecrement, onIncrement }) {
  const f = item.freshness;
  const strip = stripClass[f] || "bg-zinc-300";
  const qty = item.quantity ?? 1;
  const thumbSrc = item.thumb ? `data:image/jpeg;base64,${item.thumb}` : null;

  const stop = (e) => e.stopPropagation();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 40 }}
      className="relative bg-white pl-5 pr-4 py-4 border-b border-zinc-100"
      data-testid={`pantry-row-${item.item_id}`}
    >
      <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-r-full ${strip}`} />
      <button
        type="button"
        onClick={() => onOpen?.(item)}
        className="w-full flex items-center gap-3 text-left active:bg-zinc-50 rounded-xl transition"
        data-testid={`pantry-open-${item.item_id}`}
      >
        {/* Thumbnail */}
        {thumbSrc ? (
          <img src={thumbSrc} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" data-testid={`pantry-thumb-${item.item_id}`} />
        ) : null}
        {/* Big Quantity column */}
        <div className={`shrink-0 w-14 h-14 rounded-2xl flex flex-col items-center justify-center ${qtyBgClass[f] || "bg-zinc-100"}`}>
          <span className={`font-heading text-2xl font-semibold leading-none ${qtyTextClass[f] || "text-zinc-700"}`} data-testid={`pantry-qty-${item.item_id}`}>
            {qty}
          </span>
          <span className={`text-[9px] uppercase tracking-widest font-bold ${qtyTextClass[f] || "text-zinc-700"} opacity-70`}>
            pz
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg font-medium text-zinc-950 truncate">{item.product_name}</div>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
            <span>Scade {item.expiry_date}</span>
            {category && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-semibold"
                data-testid={`pantry-cat-${item.item_id}`}
              >
                {category.emoji ? <span>{category.emoji}</span> : null}{category.name}
              </span>
            )}
          </div>
        </div>
        <FreshnessBadge freshness={f} daysLeft={item.days_left} testId={`freshness-badge-${item.item_id}`} />
      </button>

      <div className={`flex items-center gap-2 mt-3 ${thumbSrc ? "pl-[128px]" : "pl-[68px]"}`} onClick={stop}>
        <button
          onClick={() => onDecrement(item)}
          className="w-9 h-9 rounded-full bg-zinc-100 text-zinc-800 flex items-center justify-center active:scale-90 transition disabled:opacity-30"
          aria-label="Consuma 1"
          disabled={qty <= 1}
          data-testid={`pantry-decrement-${item.item_id}`}
        >
          <Minus className="w-4 h-4" strokeWidth={2.5} />
        </button>
        <button
          onClick={() => onIncrement(item)}
          className="w-9 h-9 rounded-full bg-zinc-100 text-zinc-800 flex items-center justify-center active:scale-90 transition"
          aria-label="Aggiungi 1"
          data-testid={`pantry-increment-${item.item_id}`}
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
        </button>
        <div className="flex-1" />
        <button
          onClick={() => onConsume(item)}
          className="px-3 h-9 rounded-full bg-fresh-green-soft text-fresh-green-text text-xs font-bold flex items-center gap-1 active:scale-95 transition"
          data-testid={`pantry-finish-${item.item_id}`}
        >
          <Check className="w-3.5 h-3.5" strokeWidth={3} /> Finito
        </button>
        <button
          onClick={() => onDelete(item)}
          className="w-9 h-9 rounded-full bg-zinc-100 text-zinc-600 flex items-center justify-center active:scale-95 transition"
          aria-label="Elimina"
          data-testid={`pantry-delete-${item.item_id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

export default PantryRow;
